import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api, inr } from "../lib/api";

export const KMarkSeal = ({ size = 28 }) => (
  <span className="seal" style={{ width: size, height: size }}>
    <svg viewBox="0 0 64 64" aria-hidden="true" style={{ width: size * 0.46, height: size * 0.55 }}>
      <rect x="8" y="20" width="48" height="26" rx="11" fill="currentColor" />
    </svg>
  </span>
);

export function SiteHeader() {
  const { user } = useAuth();
  const { count, setDrawerOpen } = useCart();
  return (
    <header className="shead">
      <div className="wrap bar">
        <Link className="brand" to="/" data-testid="header-brand-link" style={{ fontSize: 15 }}>
          <KMarkSeal />
          <span className="brand-name">Kaffgum</span>
        </Link>
        <div className="hd-right">
          <Link className="back" to="/shop" data-testid="header-shop-link">Shop</Link>
          <Link
            className="hd-ic"
            to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"}
            aria-label="Account"
            data-testid="header-account-link"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <circle cx="12" cy="8" r="3.4" />
              <path d="M4.5 20c1.4-3.6 4.2-5.4 7.5-5.4s6.1 1.8 7.5 5.4" />
            </svg>
          </Link>
          <button className="cartbtn" onClick={() => setDrawerOpen(true)} aria-label="Open cart" data-testid="header-cart-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 3h2l2.4 11.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L21 7H6" />
              <circle cx="10" cy="20" r="1.3" /><circle cx="18" cy="20" r="1.3" />
            </svg>
            <span className={"badge" + (count > 0 ? " on" : "")} data-testid="cart-count-badge">{count > 0 ? count : ""}</span>
          </button>
        </div>
      </div>
    </header>
  );
}

export function CartDrawer() {
  const { items, setQty, clear, count, drawerOpen, setDrawerOpen } = useCart();
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (drawerOpen && products.length === 0)
      api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, [drawerOpen, products.length]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    const esc = (e) => e.key === "Escape" && setDrawerOpen(false);
    document.addEventListener("keydown", esc);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", esc); };
  }, [drawerOpen, setDrawerOpen]);

  // auto-close when the cart becomes empty while the drawer is open
  const prevCount = useRef(count);
  useEffect(() => {
    if (drawerOpen && count === 0 && prevCount.current > 0) setDrawerOpen(false);
    prevCount.current = count;
  }, [count, drawerOpen, setDrawerOpen]);

  const meta = (id) => products.find((p) => p.product_id === id);
  const sum = Object.entries(items).reduce((s, [id, q]) => s + q * (meta(id)?.price || 0), 0);
  if (!drawerOpen) return null;

  return (
    <>
      <div className="scrim on" onClick={() => setDrawerOpen(false)} data-testid="cart-scrim" />
      <aside className="drawer on" role="dialog" aria-modal="true" data-testid="cart-drawer">
        <div className="dhead">
          <h3>Your cart</h3>
          <button className="dx" onClick={() => setDrawerOpen(false)} aria-label="Close cart" data-testid="cart-close-btn">&times;</button>
        </div>
        <div className="lines">
          {count === 0 && <p className="empty" data-testid="cart-empty-msg">Your cart is empty.</p>}
          {Object.entries(items).map(([id, q]) => {
            const m = meta(id);
            if (!m) return null;
            return (
              <div className="line" key={id} data-testid={`cart-line-${id}`}>
                <span className="sw" style={{ background: `radial-gradient(circle at 32% 28%,#F4F1E8,${m.sw})` }} />
                <span className="info">
                  <b>{m.name}</b>
                  <span>{inr(m.price)} each</span>
                  <span className="lq">
                    <button onClick={() => setQty(id, q - 1)} aria-label={`One fewer ${m.name}`} data-testid={`cart-dec-${id}`}>&minus;</button>
                    <output>{q}</output>
                    <button onClick={() => setQty(id, q + 1)} aria-label={`One more ${m.name}`} data-testid={`cart-inc-${id}`}>+</button>
                  </span>
                </span>
                <span className="amt">{inr(q * m.price)}</span>
                <button className="rm" onClick={() => setQty(id, 0)} aria-label={`Remove ${m.name}`} data-testid={`cart-remove-${id}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
        <div className="dfoot">
          <div className="tot"><span>Total</span><b data-testid="cart-total">{inr(sum)}</b></div>
          <button className="go" disabled={count === 0} data-testid="cart-checkout-btn"
            onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}>
            Checkout
          </button>
          {count > 0 && (
            <button className="clear" onClick={clear} data-testid="cart-clear-btn">Empty cart</button>
          )}
          <p className="dnote">
            {count === 0 ? "Free delivery on orders over " + inr(499)
              : sum >= 499 ? "Free delivery included."
              : inr(499 - sum) + " more for free delivery."}
          </p>
        </div>
      </aside>
    </>
  );
}

export function SiteFooter() {
  return (
    <footer>
      <div className="foot-grid">
        <div>
          <Link className="brand" to="/" style={{ fontSize: 17 }}>
            <KMarkSeal size={26} />
            <span className="brand-name">Kaffgum</span>
          </Link>
          <p className="blurb">Caffeinated chewing gum, made for people who ran out of time before they ran out of things to do.</p>
        </div>
        <div><h5>Shop</h5><Link to="/shop">Mint</Link><Link to="/shop">Iced Latte</Link><Link to="/shop">All packs</Link></div>
        <div><h5>Learn</h5><Link to="/faq">FAQ</Link><Link to="/about">The science</Link><Link to="/about">Ingredients</Link></div>
        <div><h5>Company</h5><Link to="/about">About</Link><Link to="/contact">Contact</Link><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></div>
      </div>
      <div className="foot-base">
        <span>© 2026 Kaffgum. Roasted, milled, pressed.</span>
        <span>Sugarfree · Vegetarian</span>
      </div>
    </footer>
  );
}

export function PageShell({ children, kicker, title, sub }) {
  return (
    <div className="pg">
      <SiteHeader />
      <div className="pg-body fade-up">
        {(kicker || title) && (
          <div className="wrap pg-head">
            {kicker && <div className="pg-kicker">{kicker}</div>}
            {title && <h1 className="pg-title">{title}</h1>}
            {sub && <p>{sub}</p>}
          </div>
        )}
        {children}
      </div>
      <SiteFooter />
      <CartDrawer />
    </div>
  );
}

export const Loader = () => (
  <div className="kload"><div className="pellet-dot" /></div>
);
